import { CountBadge } from '@components/CountBadge/CountBadge';
import { Switch } from '@components/Switch/Switch';
import { useHasHover } from '@lib/hooks/sizing/useHasHover';
import { formatShortcut, SHORTCUTS } from '@lib/keyboard/shortcuts';
import { useKeyboardShortcut } from '@lib/keyboard/useKeyboardShortcut';
import { dismissOpenPopovers } from '@lib/utils/dismissOpenPopovers';
import { countOmniTokens } from '@lib/utils/omniFilter';
import ClearRoundedIcon from '@mui/icons-material/ClearRounded';
import FilterListRoundedIcon from '@mui/icons-material/FilterListRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded';
import { Button, Divider, IconButton, Menu, MenuItem, TextField, Tooltip } from '@mui/material';
import classNames from 'classnames';
import { useCallback, useEffect, useRef, useState } from 'react';
import css from './OmniFilter.module.scss';

// Resolved once at module scope: the platform cannot change mid-session, and this component
// re-renders on every keystroke.
const FIND_SHORTCUT_LABEL = formatShortcut(SHORTCUTS.openFilters);

interface OmniFilterProps {
  /** Currently applied query (`''` when nothing is applied). */
  applied: string;
  /** Called on every keystroke with the raw input value. */
  onChange: (value: string) => void;
  /** Clear the filter. */
  onClear: () => void;
  /** Input placeholder, e.g. "Name, YouTube ID, or media ID". */
  placeholder: string;
  /** Menu header. Defaults to "Filter by anything". */
  headerLabel?: string;
  /** Tooltip shown on the button when nothing is applied, e.g. "Filter media". */
  tooltip?: string;
  /** true = every typed token must match (AND); false = any token may match (OR). */
  matchAll: boolean;
  /** Toggle the match mode. */
  onMatchAllChange: (matchAll: boolean) => void;
  /**
   * Called when the popover is opened, with how the user got there. Reported outward rather than
   * tracked here because this component is presentational — the wrappers own the tracking context.
   */
  onOpen?: (via: 'keyboard' | 'click') => void;
  /**
   * Abbreviate to a bare glyph, for a caller that seats it inside a `filter-button-group` pill
   * (`_filter-popover.scss`). Drops the word "Filters", the chevron and the clear (X), keeping only
   * the glyph and the applied-token count badge.
   *
   * This is deliberately the SAME control the mobile overlays already show — glyph plus badge in a
   * divided frosted pill, growing to a short pill when the badge appears — rather than a
   * desktop-only shrunken version of the labelled form. One control, two sizes.
   *
   * The badge is what has to survive, because it is state and not decoration: "something is
   * filtered" must stay visible or the compact form silently hides media. The X can go with the
   * words because the menu this opens holds the query in an editable field, so the way back is one
   * click deeper rather than gone — which is exactly the trade mobile already makes.
   *
   * Both desktop callers set this from their row's width — `RIGHT_LABELS_BELOW` in `LibraryContent`
   * and in `LibrariesNav` — and it is the FIRST thing either row gives up, before the verb labels
   * beside it. See those constants for why that order.
   */
  compact?: boolean;
}

/**
 * Reusable "omnifilter": a "Filters" dropdown button that opens a popover with a
 * single input. Space-separated tokens filter live (applied as you type). The
 * matching/biz logic and where the query is stored live in the caller — this
 * component is purely presentational. See `@lib/utils/omniFilter` and the media
 * (`LibraryOmniFilter`) / libraries (`LibrariesOmniFilter`) wrappers.
 *
 * Owns the ⌘F / Ctrl+F shortcut that opens it (see `handleFindShortcut`). That assumes ONE
 * instance per page, which holds today: the media page mounts it in `LibraryContentHeaderRight`
 * (desktop branch only) and the libraries page in `LibrariesNav`. A second instance on one page
 * would register two listeners and open both menus — if that day comes, add an opt-out prop.
 */
export const OmniFilter = ({
  applied,
  onChange,
  onClear,
  placeholder,
  headerLabel = 'Filter by anything',
  tooltip = 'Filter',
  matchAll,
  onMatchAllChange,
  onOpen,
  compact = false,
}: OmniFilterProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);

  // The Filters pill doubles as the Menu's anchor. A ref as well as `e.currentTarget`, because
  // the keyboard path has no click to read a target from.
  const anchorRef = useRef<HTMLButtonElement>(null);
  // The query input, so a second ⌘F can select what is already there.
  const inputRef = useRef<HTMLInputElement>(null);

  // Controlled so we can suppress it while hovering the clear (X) button — otherwise
  // both this tooltip and the inner "Clear filter" tooltip show at once.
  const [tooltipOpen, setTooltipOpen] = useState(false);

  // Number of space-separated tokens in the applied query (shown as a count badge).
  const tokenCount = countOmniTokens(applied);

  // Local input state so spaces between tokens survive (the store trims leading/
  // trailing whitespace). Sync from the applied value only on external changes
  // (clear, view switch); the guard avoids clobbering whitespace the user is mid-typing.
  const [value, setValue] = useState(applied);
  useEffect(() => {
    if (applied !== value.trim()) setValue(applied);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applied]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const next = e.target.value;
      setValue(next);
      onChange(next);
    },
    [onChange],
  );

  const handleClear = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      setValue('');
      onClear();
    },
    [onClear],
  );

  /**
   * ⌘F / Ctrl+F opens this filter instead of the browser's find-in-page.
   *
   * `preventDefault()` is the assertion "we handled this", so any path that does not open the
   * filter must return without calling it, leaving native find intact. The modal case is handled
   * for us by `useKeyboardShortcut`, which stands down before we are called.
   */
  const handleFindShortcut = useCallback(
    (e: KeyboardEvent) => {
      // Already open: re-select the query, so a second press means "start over" — the same thing a
      // second ⌘F does to a browser's own find field.
      if (menuOpen) {
        e.preventDefault();
        if (e.repeat) return;
        inputRef.current?.select();
        return;
      }

      // On the Libraries page the pill is hidden by CSS rather than unmounted
      // (`LibrariesNav.module.scss` `@include mobile { display: none }`), so anchoring the Menu
      // here would position a popover against a zero-box element. `offsetParent` is null exactly
      // when the element or an ancestor is `display: none`, which is the real question being
      // asked — and it keeps holding if the row is ever hidden for some other reason.
      const anchor = anchorRef.current;
      if (!anchor?.offsetParent) return;

      e.preventDefault();
      // Holding the keys repeats the event, and the first press already did the work. Note this
      // sits AFTER preventDefault: returning early without it would let the repeat pop the
      // browser's find bar over the menu we just opened.
      if (e.repeat) return;

      // Clear any stray menu (realistically the Sort one, which sits right beside us in both
      // headers) so it isn't left floating behind ours. Safe here specifically because the
      // `menuOpen` branch above has already returned — otherwise this would dismiss us.
      dismissOpenPopovers();

      // The TextField's `autoFocus` does the focusing, exactly as it does on a click-open.
      setAnchorEl(anchor);
      onOpen?.('keyboard');
    },
    [menuOpen, onOpen],
  );

  useKeyboardShortcut({ shortcut: SHORTCUTS.openFilters, onMatch: handleFindShortcut });

  // Shown only where a physical keyboard is plausible. Hover is a proxy for that, not the same
  // question, but it is the closest one available and errs in the right direction: an iPad with a
  // Magic Keyboard has a trackpad and so reports hover, while a keyboard-less tablet reports none.
  // The shortcut itself is deliberately NOT gated on this — a trackpad-less keyboard iPad should
  // still get ⌘F, it just won't be advertised.
  const shortcutHint = useHasHover() ? FIND_SHORTCUT_LABEL : null;

  return (
    <div>
      <Tooltip
        title={applied ? `Filtering by: ${applied}` : tooltip}
        placement="top"
        arrow
        open={tooltipOpen && !menuOpen}
      >
        <Button
          ref={anchorRef}
          onClick={(e) => {
            setAnchorEl(e.currentTarget);
            onOpen?.('click');
          }}
          onMouseEnter={() => setTooltipOpen(true)}
          onMouseLeave={() => setTooltipOpen(false)}
          onFocus={() => setTooltipOpen(false)}
          className={classNames(css.filterButton, {
            [css.active]: !!applied,
            [css.compact]: compact,
          })}
          disableRipple
          // Only when compact: with the word gone, the button's own text can't name it, and a
          // bare count badge would leave the accessible name as a number. Left undefined in the
          // labelled form so the visible word stays the name (matching it is the whole point).
          aria-label={compact ? (applied ? `Filters: ${applied}` : 'Filters') : undefined}
          startIcon={<FilterListRoundedIcon className={css.filterIcon} />}
          endIcon={
            /* Nothing trails the glyph when compact — see the `compact` prop for why the clear X
               goes with the words rather than surviving them. */
            compact ? null : applied ? (
              <Tooltip title="Clear filter" placement="top" arrow>
                <IconButton
                  size="small"
                  className={css.clear}
                  onClick={handleClear}
                  onMouseEnter={() => setTooltipOpen(false)}
                  onMouseLeave={() => setTooltipOpen(true)}
                  aria-label="Clear filter"
                >
                  <ClearRoundedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : menuOpen ? (
              <KeyboardArrowUpRoundedIcon className={css.chevron} />
            ) : (
              <KeyboardArrowDownRoundedIcon className={css.chevron} />
            )
          }
        >
          {!compact && 'Filters'}
          {tokenCount > 0 && <CountBadge count={tokenCount} className={css.countBadge} />}
        </Button>
      </Tooltip>
      <Menu
        open={menuOpen}
        onClose={() => setAnchorEl(null)}
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { style: { marginTop: 8, width: 280 } } }}
      >
        <MenuItem className={css.header} disableRipple>
          <span>{headerLabel}</span>
          {shortcutHint && <span className={css.shortcutHint}>{shortcutHint}</span>}
        </MenuItem>
        <Divider className={css.divider} />
        <div className={css.inputWrap}>
          <TextField
            inputRef={inputRef}
            placeholder={placeholder}
            variant="filled"
            autoComplete="off"
            autoFocus
            fullWidth
            value={value}
            onChange={handleChange}
            onKeyDown={(e) => {
              if (e.key === 'Escape') return; // let the Menu handle close
              e.stopPropagation(); // keep typing out of the Menu's typeahead
              if (e.key === 'Enter') {
                e.preventDefault();
                setAnchorEl(null);
              }
            }}
          />
        </div>
        <Divider className={css.divider} />
        <div className={css.matchModeRow}>
          <span className={css.matchModeLabel} onClick={() => onMatchAllChange(!matchAll)}>
            Match all terms
          </span>
          <Switch checked={matchAll} onChange={onMatchAllChange} />
        </div>
      </Menu>
    </div>
  );
};
